import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import { Who } from '../shared/cells';
import { useSortable } from '../shared/use_sortable';
import { fetch_l1 } from '../api';
import { fmt_count } from '../format';
import { l1_params } from '../overview/params';
import { EMAIL_LOGS_NOTE, email_error_view, teacher_rows_view } from './adapters';
import {
  EMAIL_LOGS_URL_KEYS,
  email_logs_filter_summary,
  email_logs_filters_on,
  email_logs_query,
  filter_teacher_rows,
  parse_email_logs_sort
} from './params';
import { useInitialQuery, useQueryMirror } from '../shared/use_query_mirror';
import { FilterBar } from '../shared/controls';
import { summary_line } from '../payouts/summary_line';
import PreviewDialog from './preview_dialog';
import TextEditorDialog from './text_editor_dialog';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import { email_logs_columns, email_logs_csv_name } from './csv';
import './email_logs.css';

/* the Email logs tab (/teacher-payments/email-logs). */
const LOAD_FAILED_TEXT = 'Could not load the teacher list.';
// The teacher list comes from the L1 overview read (there is no email list endpoint), capped here.
const LIST_CAP = 500;

// Who on the left, the Preview action in its own track on the right; a one-row card on phones.
const COLUMNS = [
  {
    key: 'teacher',
    role: 'identifier',
    label: 'Teacher',
    sort_key: 'teacher_name',
    render: (r) => <Who name={r.teacher_name} meta={[r.teacher_slug]} />
  },
  {
    key: 'actions',
    label: 'Email',
    role: 'actions',
    align: 'right',
    track: '120px',
    render: (r, on_preview) => (
      <span className="tp-c-acts">
        <button
          type="button"
          className="tp-link"
          aria-label={`Preview email for ${r.teacher_name}`}
          onClick={() => on_preview(r)}
        >
          Preview
        </button>
      </span>
    ),
    // Cards: a full-size outlined button in the footer (the table keeps the link).
    card_render: (r, on_preview) => (
      <button
        type="button"
        className="tp-btn-out"
        aria-label={`Preview email for ${r.teacher_name}`}
        onClick={() => on_preview(r)}
      >
        Preview
      </button>
    )
  }
];

const EmailLogsTab = ({ pay_month, month_label, on_error, on_open_payment_emails }) => {
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [rows, setRows] = useState([]);
  const [error_text, setErrorText] = useState('');
  const [q, setQ] = useState('');
  const [preview_row, setPreviewRow] = useState(null);
  const [editing_text, setEditingText] = useState(false);
  const seq = useRef(0);
  const heading_ref = useRef(null);

  const load = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setStatus('loading');
    fetch_l1(l1_params({ pay_month, sort: { key: 'teacher_name', dir: 'asc' } }, LIST_CAP, 0))
      .then((data) => {
        if (mine !== seq.current) return;
        setRows(teacher_rows_view(data).rows);
        setStatus('ready');
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        const v = email_error_view(e, LOAD_FAILED_TEXT);
        if (v.kind === 'permission') {
          on_error(e, LOAD_FAILED_TEXT);
          return;
        }
        setErrorText(v.text);
        setStatus('error');
      });
  }, [pay_month, on_error]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => filter_teacher_rows(rows, q), [rows, q]);
  const url_sort = useInitialQuery(parse_email_logs_sort);
  const { sort, on_sort, sorted_rows } = useSortable(filtered, COLUMNS, url_sort);
  useQueryMirror(EMAIL_LOGS_URL_KEYS, email_logs_query(sort));

  const columns = COLUMNS.map((c) =>
    c.key === 'actions'
      ? {
          ...c,
          render: (r) => c.render(r, setPreviewRow),
          card_render: (r) => c.card_render(r, setPreviewRow)
        }
      : c
  );

  return (
    <div className="tp-em">
      {/* the send switch owns delivery, so this tab only points at it (no second, stale claim). */}
      <div className="tp-banner tp-banner-info">
        {EMAIL_LOGS_NOTE}{' '}
        {on_open_payment_emails ? (
          <button type="button" className="tp-link" onClick={on_open_payment_emails}>
            Open Payment emails
          </button>
        ) : null}
      </div>

      <FilterBar
        label="Email log filters"
        summary={summary_line(
          status === 'ready'
            ? email_logs_filter_summary({ total: sorted_rows.length, month_label, q })
            : null
        )}
      >
        <div className="tp-fsearch">
          <input
            id="tp-em-q"
            className="tp-input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by teacher name"
            aria-label="Search teachers"
          />
        </div>
        {email_logs_filters_on(q) ? (
          <button type="button" className="tp-link tp-fbar-end" onClick={() => setQ('')}>
            Clear filters
          </button>
        ) : null}
      </FilterBar>

      <div className="tp-card tp-em-list">
        <div className="tp-card-hd">
          <span className="tp-card-hd-main">
            <span
              className="tp-card-hd-title"
              role="heading"
              aria-level={2}
              tabIndex={-1}
              ref={heading_ref}
            >
              Email logs
            </span>
            <span className="tp-card-hd-sub">
              {month_label}
              {status === 'ready'
                ? ` · ${fmt_count(sorted_rows.length)} ${sorted_rows.length === 1 ? 'teacher' : 'teachers'}`
                : ''}
            </span>
          </span>
          <span className="tp-card-hd-acts">
            {status === 'ready' ? (
              <DownloadCsvButton
                columns={email_logs_columns(pay_month)}
                rows={sorted_rows}
                file_name={email_logs_csv_name(pay_month)}
              />
            ) : null}
            <button type="button" className="tp-btn-out" onClick={() => setEditingText(true)}>
              Edit email text
            </button>
          </span>
        </div>
        {status === 'loading' ? <CustomLoading /> : null}
        {status === 'error' ? (
          <div className="tp-em-body">
            <LoadFailed text={error_text} on_retry={load} />
          </div>
        ) : null}
        {status === 'ready' ? (
          <DataTable
            columns={columns}
            rows={sorted_rows}
            rowKey={(r) => r.teacher_uuid}
            sort={sort}
            on_sort={on_sort}
            emptyText="No teacher payment rows for this month."
            card_below={660}
          />
        ) : null}
      </div>

      {preview_row ? (
        <PreviewDialog
          pay_month={pay_month}
          month_label={month_label}
          teacher_uuid={preview_row.teacher_uuid}
          teacher_name={preview_row.teacher_name}
          on_close={() => setPreviewRow(null)}
          on_focus_lost={() =>
            heading_ref.current && heading_ref.current.focus({ preventScroll: true })
          }
          on_error={on_error}
        />
      ) : null}

      {editing_text ? (
        <TextEditorDialog
          pay_month={pay_month}
          month_label={month_label}
          on_close={() => setEditingText(false)}
          on_focus_lost={() =>
            heading_ref.current && heading_ref.current.focus({ preventScroll: true })
          }
          on_error={on_error}
        />
      ) : null}
    </div>
  );
};

export default EmailLogsTab;
